import { captureMessage } from '@sentry/react';
import { produce } from 'immer';
import type { CardEditorType, StoryModel, StoryVersionType } from 'types/story';
import type { AdminReducerState } from 'admin/reducers';
import { StoryFacade } from 'utils/facades/story-facade';
import { createThunkAction } from 'admin/actions/helpers';
import { UPDATE_PUBLISHED_STORY } from 'admin/constants/actions';
import { processStory } from 'admin/actions/story/get-story';
import { updatePublishedStory as updatePublishedStoryEndpoint } from 'admin/resources';
import { injectContentHeight } from 'admin/actions/card-editor/update-editable-card-data';
import { getMergeOfEditableStoryWithCardEditor } from 'admin/actions/card-editor/card-editor-sync';
import { storyEditorChange, storyEditorFormChange } from 'admin/actions/story/update-editable-story';
import { flushStoryEditorSave } from 'admin/actions/story/flush-story-editor-save';
import { catchUpStory } from 'admin/actions/story/catch-up-story';
import { toast } from 'admin/components/common/MessageContainer/toast';
import { isEditOperationsSave } from 'admin/utils/story-save-mechanism';
import { selectOpCounter } from 'admin/reducers/story-editor/selectors';
import { selectCurrentVersion } from 'admin/reducers/version-reducer';
import { selectActiveOrganization } from 'admin/reducers/user/selectors';
import { onUpdateVersionError } from './update-latest-story';
import { preparePublishedPresetFonts } from './story-preset-fonts';

export type UpdatePublishedStoryParams = {
	isCardEditor?: boolean;
};

export function getStorySnapshotForPublish(
	state: AdminReducerState,
	params: UpdatePublishedStoryParams,
	version = selectCurrentVersion(state)
): StoryModel | null {
	if (!state.storyEditor.story) return null;
	return params.isCardEditor
		? getMergeOfEditableStoryWithCardEditor(
				state.storyEditor.story,
				injectContentHeight(state.cardEditor.present) as CardEditorType,
				version
			)
		: state.storyEditor.story;
}

export const updatePublishedStory = createThunkAction<StoryVersionType, UpdatePublishedStoryParams>({
	type: UPDATE_PUBLISHED_STORY,
	payloadCreator: async (params, { getState, dispatch }) => {
		if (isEditOperationsSave) {
			// Publish what the server holds: save queued edits, then pick up other writers' changes.
			const { success } = await dispatch(flushStoryEditorSave());
			if (!success) {
				toast.error('Changes are not saved yet. Try publishing again.');
				return { success: false };
			}
			await dispatch(catchUpStory(getState().storyEditor.story?.id));
		}

		const state = getState();
		const version = selectCurrentVersion(state);
		const opCounter = selectOpCounter(state);

		const storyEditor = getStorySnapshotForPublish(state, params, version);
		if (!storyEditor) {
			return { success: false };
		}

		const source = storyEditor.storyVersions[version];
		const organization = selectActiveOrganization(state);
		const publicationSettings = preparePublishedPresetFonts({
			data: source.data,
			settings: source.settings,
			organizationFonts:
				organization?.id === storyEditor.organizationId ? organization?.metadata?.fonts : undefined,
		});
		const prepared =
			publicationSettings === source.settings
				? storyEditor
				: produce(storyEditor, draft => {
						draft.storyVersions[version].settings = publicationSettings;
					});

		// process data #2 (see processStory())
		const Story = new StoryFacade(prepared, version);
		let { settings, storyId, data: storyData } = Story;

		try {
			({ data: storyData, settings, storyId } = processStory(Story, { parseSystemFontsUsage: true }));
		} catch (e) {
			console.error(e);
			captureMessage('updatePublishedStory:processStory() is failed');
		}

		// configure payload
		const payload = {
			storyData,
			settings,
			opCounter,
		};

		await updatePublishedStoryEndpoint.params({ storyId }).send(payload);

		/*
		 Configure and update state with a "published-like" version to avoid request of getStory after publish.
		 Note: This part can be omitted if backend will be configured to return published version data in response.
		 */
		const published = Story.base.storyVersions[StoryFacade.VERSIONS.published] as StoryVersionType | undefined;
		const updatedAt = new Date().toISOString();
		const versionPath = `storyVersions.${StoryFacade.VERSIONS.published}`;
		const nextPublished: StoryVersionType = {
			...published,
			storyId,
			updatedAt,
			data: payload.storyData,
			settings: payload.settings,
			changelog: published?.changelog || null,
			createdAt: published?.createdAt || updatedAt,
			version: published?.version || StoryFacade.VERSIONS.published,
		};

		// update form.storyEditor
		dispatch(storyEditorFormChange({ path: versionPath, data: nextPublished }));
		// update storyEditor
		dispatch(storyEditorChange({ path: versionPath, data: nextPublished }));

		if (isEditOperationsSave) {
			// The reducer does not advance opCounter in this mode.
			await dispatch(catchUpStory(storyId));
		}

		return { success: true, result: nextPublished };
	},
	onError: onUpdateVersionError,
});
